<template>
    <div>
        <div class="card">
            <div class="mb-3" v-if="loading">
                <Loading type="line" :center="false" v-show="loading" />
            </div>
            <section v-if="groups.length">
                <PayableReceivableTotalization :totalizations="totalizations" />
            </section>

            <section v-if="!loading" class="payable-receivable-table-topbar">
                <div v-if="groups.length" class="payable-receivable-table-topbar-left">
                    <b-form-checkbox v-model="selectAllVisible" />
                    <p class="ml-2 payable-receivable-table-topbar-select-all">Selecionar tudo</p>
                </div>
                <div v-else></div>
                <div class="payable-receivable-table-topbar-right">
                    <TotalRecordsBadge :total="totalRecords" />
                    <span @click="openModal('config')" class="payable-receivable-table-tools-icon">
                        <i class="fa-solid fa-gear"></i>
                    </span>
                </div>
            </section>

            <div v-if="!loading && !groups.length" class="payable-receivable-table-empty">
                Nenhum registro encontrado
            </div>
            <section v-for="(group, idx) in groups" :key="group.key" class="payable-receivable-table-day">
                <header class="payable-receivable-table-day-header">
                    <div class="payable-receivable-table-day-left">
                        <b-form-checkbox v-model="selectAllGroup[group.key]" @change="selectGroup(group)" />
                        <span class="payable-receivable-table-daylabel">{{ group.dayLabel }}</span>
                    </div>
                </header>
                <PayableReceivableRow v-for="row in group.items" :key="row.id" :item="row" :showBalance="showBalance"
                    @edit="onEdit" />
            </section>

            <Pagination :totalRecords="totalRecords" />
        </div>

        <BottomActionsBar :selected="selected" @close="closeSelectedActions">
            <template #header-right>
                <Badge :type="selectedTotalBadgeType" size="medium"
                    :title="'Total selecionado: ' + $options.filters.currency(selectedTotalValue)" />
            </template>
            <div class="payable-receivable-table-actions">
                <div v-if="hasRule('Billet')">
                    <Button _key="btnOpenBillet" :title="'Boleto Bancário'" classIcon="fa-solid fa-barcode"
                        type="primary" size="small" :clicked="openBillet" />
                </div>
                <ActionsSelected :selected="selected" :actions="availableActions" />
                <div v-if="selected.length === 1">
                    <Tooltip title="Clonar" placement="top">
                        <Button _key="btnClonePayableReceivable" classIcon="fa-solid fa-clone" type="edit" size="small"
                            :clicked="openClone" />
                    </Tooltip>
                </div>
                <div v-if="selected.length === 1">
                    <Tooltip title="Recibo" placement="top">
                        <Button _key="btnReceipt" classIcon="fa-solid fa-file-invoice-dollar" type="info" size="small"
                            :clicked="openReceipt" />
                    </Tooltip>
                </div>

            </div>
        </BottomActionsBar>

        <Modal :title="billetModalTitle" :width="900" :onHideModal="onHideBillet" v-show="showModal('openBillet')">
            <Billet origin="payableReceivable" :selected="selected" v-if="showModalBillet" v-model="selectedOption" />
        </Modal>

        <Modal title="Configuração" :width="500" :height="750" :onHideModal="onHideConfig" v-if="showModal('config')">
            <PayableReceivableConfig />
        </Modal>

        <PrintPreview v-if="printFinance" module="Finance" :payableReceivableId="payableReceivableId"
            :onHideModal="onHidePrintPreview" />
    </div>
</template>

<script>
import Molded from "@/ui/layout/Molded";
import Pagination from "@/ui/shared/Pagination.vue";
import Loading from "@/ui/shared/Loading.vue";
import Modal from "@/ui/forms/Modal";
import Button from "@/ui/forms/Button";
import BottomActionsBar from "@/ui/layout/BottomActionsBar.vue";
import ActionsSelected from "@/ui/shared/actions/ActionsSelected.vue";
import Tooltip from "@/ui/layout/Tooltip.vue";
import Badge from "@/ui/layout/Badge.vue";
import PayableReceivableConfig from "./PayableReceivableConfig.vue";
import Billet from "../../../../components/modules/billing/billet/Billet.vue";
import PrintPreview from "../../../../components/modules/adm/document/PrintPreview.vue";
import PayableReceivableRow from "./PayableReceivableRow.vue";

import PayableReceivableService from "../../services/payableReceivable.service.js";
import { getPayableReceivableConfigSelection, isPaidOrReceived } from "../../services/payableReceivableList.config.js";

import { mapGetters, mapMutations, mapState } from "vuex";
import TotalRecordsBadge from "@/ui/layout/TotalRecordsBadge.vue";
import PayableReceivableTotalization from "./PayableReceivableTotalization.vue";

import PayableReceivableMark from "./PayableReceivableMark.vue";
import PayableReceivablePartialPayment from "./PayableReceivablePartialPayment.vue";
import PayableReceivableMarkInstallments from "./PayableReceivableMarkInstallments.vue";

function parseDueDate(raw) {
    if (raw instanceof Date) {
        return raw;
    }

    if (typeof raw === "string") {
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);

        if (iso) {
            return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
        }

        if (br) {
            return new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
        }

        return new Date(raw);
    }

    return new Date(NaN);
}

const initialConfigSelection = getPayableReceivableConfigSelection();

export default {
    name: "PayableReceivableTable",
    components: { Molded, PayableReceivableRow, BottomActionsBar, ActionsSelected, Pagination, Loading, Modal, Button, Tooltip, Badge, PayableReceivableConfig, Billet, PrintPreview, TotalRecordsBadge, PayableReceivableTotalization },
    props: {
        items: { type: Array, default: () => [] },
        totalizations: { type: Array, default: () => [] },
        showClosed: { type: Boolean, default: false },
        hasPaidNameFilterActive: { type: Boolean, default: false },
        totalRecords: { type: Number, default: 0 },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            actions: [
                {
                    type: "delete",
                    title: "Remover",
                    description: "Remover",
                    tooltip: "Remover",
                    url: PayableReceivableService.urlDelete,
                    method: "delete",
                },
                {
                    type: "component",
                    description: "Marcar como pago",
                    tooltip: "Marcar como pago",
                    url: PayableReceivableService.urlMarkAsPaid,
                    method: "post",
                    classIcon: "fa-solid fa-check-double",
                    buttonType: "success",
                    backgroundColor: "var(--color-success)",
                    preProcessComponent: PayableReceivableMark,
                },
                {
                    type: "component",
                    description: "Desmarcar como pago",
                    tooltip: "Desmarcar como pago",
                    url: PayableReceivableService.urlUnmarkAsPaid,
                    method: "post",
                    classIcon: "fa-solid fa-rotate-left",
                    buttonType: "edit",
                    backgroundColor: "var(--color-brand)",
                },
                {
                    type: "component",
                    description: "Quitar parcelas",
                    tooltip: "Quitar as últimas parcelas em aberto",
                    url: PayableReceivableService.urlMarkInstallmentsAsPaid,
                    method: "post",
                    classIcon: "fa-solid fa-layer-group",
                    buttonType: "success",
                    backgroundColor: "var(--color-success)",
                    preProcessComponent: PayableReceivableMarkInstallments,
                    preProcessModalWidth: 900,
                },
                {
                    type: "component",
                    description: "Baixa Parcial",
                    tooltip: "Baixa Parcial",
                    url: PayableReceivableService.urlPartialPaymentCreate,
                    method: "post",
                    classIcon: "fa-solid fa-scissors",
                    buttonType: "info",
                    backgroundColor: "#EAB308",
                    preProcessComponent: PayableReceivablePartialPayment,
                    preProcessModalWidth: 900,
                },
            ],
            selectedOption: 0,
            showModalBillet: false,
            printFinance: false,
            payableReceivableId: "",
            selectAllGroup: {},
            showBalance: initialConfigSelection.includes("showBalance"),
        };
    },
    computed: {
        ...mapGetters("generic", ["showModal", "event"]),
        ...mapState("generic", ["selected", "executedSearch", "clearedSearch", "search", "paginations"]),
        ...mapGetters("user", ["hasRule"]),
        selectAllVisible: {
            get() {
                return this.allRowIds.length > 0 && this.allRowIds.every((id) => this.selected.includes(id));
            },
            set(value) {
                this.addSelected(value ? [...this.allRowIds] : []);
                this.syncGroupSelection();
            },
        },
        allRowIds() {
            return this.groups.flatMap((group) => group.items.map((item) => item.id));
        },
        groups() {
            const sourceItems = (this.hasPaidNameFilterActive || this.showClosed)
                ? this.items
                : this.items.filter((item) => !this.isClosedItem(item));
            const mapped = sourceItems.map((item) => {
                const typeLower = (item.typeName || item.type || "").toLowerCase();
                const isReceivable = typeLower === "receber";


                return {
                    ...item,
                    _d: parseDueDate(item.dueDate),
                    type: isReceivable ? "receivable" : "payable",
                    icon: isReceivable ? "fa-solid fa-arrow-trend-up" : "fa-solid fa-arrow-trend-down",
                    colorIcon: isReceivable ? "var(--color-success)" : "var(--color-danger)",
                };
            });

            const map = new Map();
            for (const it of mapped) {
                const key = this.keyFromDate(it._d);
                if (!map.has(key)) map.set(key, []);
                map.get(key).push(it);
            }

            const fmtWeek = new Intl.DateTimeFormat("pt-BR", { weekday: "long" });
            const fmtMonth = new Intl.DateTimeFormat("pt-BR", { month: "long" });

            return Array.from(map.entries())
                .map(([key, items]) => ({
                    key,
                    items,
                    dayNumber: items[0]._d.getDate(),
                    weekday: isNaN(items[0]._d) ? "" : fmtWeek.format(items[0]._d),
                    month: isNaN(items[0]._d) ? "" : fmtMonth.format(items[0]._d),
                    dayLabel: isNaN(items[0]._d)
                        ? ""
                        : `Dia ${items[0]._d.getDate()} de ${fmtMonth.format(items[0]._d)}, ${fmtWeek.format(items[0]._d)}`,
                }));
        },
        selectedRows() {
            const rowsById = new Map(
                this.groups
                    .flatMap((group) => group.items)
                    .map((item) => [item.id, item])
            );

            return this.selected
                .map((id) => rowsById.get(id))
                .filter(Boolean);
        },
        markAsPaidIds() {
            return this.selectedRows
                .filter((item) => !isPaidOrReceived(item))
                .map((item) => item.id);
        },
        unmarkAsPaidIds() {
            return this.selectedRows
                .filter((item) => isPaidOrReceived(item))
                .map((item) => item.id);
        },
        showMarkAsPaid() {
            return this.markAsPaidIds.length > 0;
        },
        showUnmarkAsPaid() {
            return this.unmarkAsPaidIds.length > 0;
        },
        showPartialPayment() {
            return this.selectedRows.length === 1;
        },
        showMarkInstallments() {
            if (this.selectedRows.length !== 1) return false;

            const row = this.selectedRows[0];

            return !!row.hasInstallments && !isPaidOrReceived(row);
        },
        selectedTotalValue() {
            return this.selectedRows.reduce((total, item) => {
                return total + (Number(item?.value) || 0);
            }, 0);
        },
        selectedTotalBadgeType() {
            if (this.selectedTotalValue < 0) return "payable";
            if (this.selectedTotalValue > 0) return "receivable";
            return "default";
        },
        availableActions() {
            return this.actions.filter((action) => {
                if (action.url === PayableReceivableService.urlMarkAsPaid) {
                    return this.showMarkAsPaid;
                }

                if (action.url === PayableReceivableService.urlUnmarkAsPaid) {
                    return this.showUnmarkAsPaid;
                }

                if (action.preProcessComponent === PayableReceivablePartialPayment) {
                    return this.showPartialPayment;
                }

                if (action.preProcessComponent === PayableReceivableMarkInstallments) {
                    return this.showMarkInstallments;
                }

                return true;
            });
        },
        billetModalTitle() {
            if (this.selectedOption == 1) return "Emitir Boleto";
            if (this.selectedOption == 2) return "Alterar Boleto";
            if (this.selectedOption == 3) return "Cancelar Boleto";
            return "Seleciona uma opção para boleto bancário";
        },
    },
    methods: {
        ...mapMutations("generic", ["openModal", "hideModal", "removeLoading", "cleanSelected", "addSelected", "addEvent"]),
        isClosedItem(item) {
            return isPaidOrReceived(item);
        },
        keyFromDate(d) {
            if (!d || isNaN(d)) return "invalid";
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        },
        onEdit(item) {
            this.addEvent({ name: "payableReceivableUpdate", data: { id: item.id } });
        },
        syncGroupSelection() {
            const next = {};
            this.groups.forEach((group) => {
                next[group.key] = group.items.length > 0 && group.items.every((item) => this.selected.includes(item.id));
            });
            this.selectAllGroup = next;
        },
        selectGroup(group) {
            const allSelected = this.selectAllGroup[group.key];
            const ids = group.items.map(i => i.id);
            if (allSelected) {
                const merged = [...new Set([...this.selected, ...ids])];
                this.addSelected(merged);
            } else {
                this.addSelected(this.selected.filter(id => !ids.includes(id)));
            }
            this.syncGroupSelection();
        },
        openBillet() {
            this.showModalBillet = true;
            this.openModal("openBillet");
            this.removeLoading(["btnOpenBillet"]);
        },
        openReceipt() {
            this.addEvent({ name: "receiptFinance", data: { id: this.selected[0] } });
            this.removeLoading(["btnReceipt"]);
            this.closeSelectedActions();
        },
        openClone() {
            this.addEvent({ name: "payableReceivableClone", data: { id: this.selected[0] } });
            this.removeLoading(["btnClonePayableReceivable"]);
            this.closeSelectedActions();
        },
        onHideBillet() {
            this.showModalBillet = false;
        },
        onHideConfig() {
            this.hideModal("config");
        },
        onHidePrintPreview() {
            this.printFinance = false;
        },
        closeSelectedActions() {
            this.addSelected([]);
            this.selectAllGroup = {};
        },
    },
    watch: {
        selected() {
            this.syncGroupSelection();
        },
        groups() {
            this.syncGroupSelection();
        },
        event: {
            handler(event) {
                if (event.name === "deselectAll") {
                    this.addSelected([]);
                    this.selectAllGroup = {};
                }
                if (event.name === "canceledBillet" || event.name === "updatedBillet" || event.name === "generatedPdfBillet") {
                    this.cleanSelected();
                    this.hideModal();
                    this.showModalBillet = false;
                }
                if (event.name === "page-click") {
                    this.$emit("page-change", event.data);
                }
                if (event.name === "postApi" || event.name === "putApi" || event.name === "deleteAllApi" || event.name === "actionsUpdated") {
                    this.$emit("reload");
                }
                if (event.name === "receiptFinance") {
                    this.printFinance = true;
                    this.payableReceivableId = event.data.id;
                }
                if (event.name === "emailSent") {
                    this.printFinance = false;
                }
                if (event.name === "payableReceivableToggleBalance") {
                    this.showBalance = !!event.data?.showBalance;
                }
            },
            deep: true,
        },
        executedSearch() {
            if (!this.search.content) {
                this.$emit("clear-search");
                this.removeLoading(["btnSearch"]);
                return;
            }
            this.$emit("set-search", {
                search: this.search.content,
                filter: this.search.filter?.id || "contains",
            });
        },
        clearedSearch() {
            this.$emit("clear-search");
            this.removeLoading(["btnClean"]);
        },
        "paginations.totalPerPage"(val) {
            this.$emit("set-total-per-page", val);
        },
    },
};
</script>

<style scoped>
.payable-receivable-table-actions {
    display: flex;
    flex-wrap: wrap;
}

.payable-receivable-table-empty {
    padding: 24px;
    text-align: center;
    color: var(--text-muted);
    font-size: 14px;
}

.payable-receivable-table-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 0 8px;
    background: var(--bg-base);
    border-bottom: 1px solid var(--border-subtle);
}

.payable-receivable-table-topbar-left,
.payable-receivable-table-topbar-right {
    display: flex;
    align-items: center;
}

.payable-receivable-table-topbar-select-all {
    color: var(--text-primary);
}

.payable-receivable-table-topbar-right {
    justify-content: flex-end;
    gap: 8px;
}

.payable-receivable-table-day {
    margin-bottom: 4px;
}

.payable-receivable-table-day-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0;
    background: var(--bg-elevated);
}

.payable-receivable-table-day-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.payable-receivable-table-tools-icon {
    font-size: 20px;
    cursor: pointer;
    color: var(--text-secondary);
}

.payable-receivable-table-daylabel {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    text-transform: capitalize;
}

.card {
    border: 1px solid var(--border-color);
    background-color: var(--bg-base);
    border-radius: 10px !important;
    padding: 16px;
    box-shadow: var(--shadow-sm);
}

@media (max-width: 768px) {
    .card {
        padding: 12px;
    }
}
</style>
